import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getDataAPI, postDataAPI, putDataAPI } from '../../Helpers/FetchAPI';
import useUser from '../../Hooks/useUser.js';
import { logger } from '../../utils/logger.js';
import { isCreditExpired } from '../../utils/creditExpiration';

export const CreditsContext = createContext({
  credits: [],
  isLoading: false,
  error: null,
  getPendingCredits: () => {},
  handleUseCredit: () => {},
  handleRevokeCredit: () => {}
});

const CreditsContextProvider = ({ children }) => {
  const [credits, setCredits] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [callback, setCallback] = useState(true);

  const { currentUser } = useUser();
  const queryClient = useQueryClient();

  const getCredits = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!currentUser) return;

      const res = await getDataAPI(`/credit/user/${currentUser._id}`);

      setCredits(res.data);
      setCallback(false);
    } catch (err) {
      logger.error('Error getting credits for user', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, callback]);

  const getPendingCredits = useCallback(
    async (callbackFN) => {
      await getDataAPI(`/credit/pendingcredits/${currentUser._id}`).then((res) => {
        callbackFN(res.data);
      });
    },
    [currentUser]
  );

  const handleUseCredit = useCallback(
    async (params) => {
      // Check if credit is expired before attempting to redeem
      if (isCreditExpired(params.credit)) {
        throw new Error('This credit has expired and cannot be redeemed');
      }

      if (params.credit.workbook_version === 2) {
        await postDataAPI(`/credit/v2/redeem/`, {
          credit_id: params.credit._id,
          user_id: currentUser._id
        });
      } else {
        await putDataAPI(`/credit/usecredit/${currentUser._id}`, params);
      }

      await Promise.all([
        getCredits(),
        queryClient.invalidateQueries({ queryKey: ['authenticatedUser'] }),
        queryClient.invalidateQueries({ queryKey: ['userClaimedWorkbooks', 'list'] })
      ]);

      setCallback(false);
    },
    [currentUser, getCredits, queryClient]
  );

  const handleRevokeCredit = useCallback(async (params) => {
    await putDataAPI(`/credit/revokecredit`, params);
  }, []);

  const handleAssignCredit = useCallback(
    async (params) => {
      const payload = {
        user: currentUser,
        redemption_email: params.to_email,
        redemption_code: params.credit_id
      };
      await putDataAPI(`/credit/transfer/${params.credit_id}`, payload);
    },
    [currentUser]
  );

  useEffect(() => {
    let isActive = true;

    if (isActive) {
      if (currentUser || callback) {
        getCredits();
      }
    }

    return () => {
      isActive = false;
    };
  }, [callback, currentUser, getCredits]);

  const refreshCredits = useCallback(() => getCredits(), [getCredits]);

  const value = useMemo(() => {
    return {
      credits,
      refreshCredits,
      isLoading,
      error,
      getPendingCredits,
      handleUseCredit,
      handleAssignCredit,
      handleRevokeCredit
    };
  }, [credits, isLoading, error, getPendingCredits, handleUseCredit, handleRevokeCredit, handleAssignCredit]);
  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
};

export default CreditsContextProvider;
